import { useState, useEffect, useCallback } from 'react';
import type { IntegrationLog, IntegrationLogFilters, IntegrationLogType, IntegrationLogStatus } from '@yawi/types';
import { useServices } from '@/app/providers';

interface UseIntegrationLogsReturn {
  logs: IntegrationLog[];
  isLoading: boolean;
  totalCount: number;
  filters: IntegrationLogFilters;
  setFilters: (filters: IntegrationLogFilters) => void;
  refresh: () => void;
}

// Mock data for development
const generateMockLogs = (): IntegrationLog[] => {
  const types: IntegrationLogType[] = ['EMAIL', 'SMS', 'WHATSAPP', 'PUSH', 'PAYMENT'];
  const statuses: IntegrationLogStatus[] = ['SUCCESS', 'FAILURE', 'PENDING'];
  const providers: Record<IntegrationLogType, string[]> = {
    EMAIL: ['SENDGRID', 'BREVO', 'MAILERSEND', 'SMTP'],
    SMS: ['TWILIO'],
    WHATSAPP: ['META_WHATSAPP', 'TWILIO_WHATSAPP'],
    PUSH: ['FIREBASE'],
    PAYMENT: ['STRIPE', 'MERCADOPAGO', 'CONEKTA'],
  };

  return Array.from({ length: 50 }, (_, i) => {
    const type = types[Math.floor(Math.random() * types.length)];
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const provider = providers[type][Math.floor(Math.random() * providers[type].length)];

    return {
      id: `log-${i + 1}`,
      integrationId: `int-${Math.floor(Math.random() * 5) + 1}`,
      type,
      provider,
      status,
      recipient: `user${i}@example.com`,
      subject: type === 'EMAIL' ? `Notificación #${i + 1}` : undefined,
      errorMessage: status === 'FAILURE' ? 'Connection timeout' : undefined,
      latencyMs: Math.floor(Math.random() * 2000) + 100,
      createdAt: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
    };
  }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
};

export function useIntegrationLogs(): UseIntegrationLogsReturn {
  const { integrationRepository } = useServices();
  const [logs, setLogs] = useState<IntegrationLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState<IntegrationLogFilters>({
    page: 1,
    limit: 20,
  });

  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await integrationRepository.getLogs(filters);
      setLogs(response.data);
      setTotalCount(response.total);
    } catch (err) {
      console.error('Failed to fetch integration logs:', err);
      // Use mock data as fallback
      const mockLogs = generateMockLogs();
      let filteredLogs = mockLogs;

      if (filters.type) {
        filteredLogs = filteredLogs.filter(log => log.type === filters.type);
      }
      if (filters.status) {
        filteredLogs = filteredLogs.filter(log => log.status === filters.status);
      }
      if (filters.integrationId) {
        filteredLogs = filteredLogs.filter(log => log.integrationId === filters.integrationId);
      }

      const page = filters.page || 1;
      const limit = filters.limit || 20;
      const start = (page - 1) * limit;
      const end = start + limit;

      setLogs(filteredLogs.slice(start, end));
      setTotalCount(filteredLogs.length);
    } finally {
      setIsLoading(false);
    }
  }, [integrationRepository, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return {
    logs,
    isLoading,
    totalCount,
    filters,
    setFilters,
    refresh: fetchLogs,
  };
}
